import { useEffect, useState, useMemo } from "react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Typeahead, STAFF_ROLES } from "@/components/Typeahead";
import { toast } from "sonner";
import { Plus, Activity, Trash2, Edit } from "lucide-react";
import { formatDate } from "@/lib/ui";

const SERVICE_TYPES = [
  "consultation", "assessment", "follow_up", "intake_visit", "therapy_session",
  "home_visit", "phone_consult", "documentation", "care_coordination", "other",
];

function isoAt(date, time) {
  if (!date || !time) return null;
  return new Date(`${date}T${time}:00`).toISOString();
}
function hoursBetween(startIso, endIso) {
  if (!startIso || !endIso) return 0;
  const ms = new Date(endIso) - new Date(startIso);
  return ms > 0 ? ms / (1000 * 60 * 60) : 0;
}
function fmtHours(h) { return h ? `${h.toFixed(2)} hr` : "—"; }
function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export default function VisitsTab({ clientId, isReadOnly, onChange }) {
  const { currentOrgId, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());

  function blank() {
    const now = new Date();
    const dstr = now.toISOString().slice(0, 10);
    const t1 = now.toTimeString().slice(0, 5);
    const t2 = new Date(now.getTime() + 30 * 60000).toTimeString().slice(0, 5);
    return {
      date: dstr, start: t1, end: t2,
      staff_user_id: user?.id || "",
      staff_role: "Care Coordinator",
      service_type: "consultation",
      notes: "",
    };
  }

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("visits")
      .select("id, client_id, service_type, occurred_at, start_time, end_time, duration_minutes, staff_user_id, staff_role, status, summary, profiles:staff_user_id(full_name)")
      .eq("client_id", clientId)
      .order("occurred_at", { ascending: false });
    setRows(data || []);
    setLoading(false);
  };

  const loadStaff = async () => {
    const { data: members } = await supabase
      .from("organization_members")
      .select("user_id, role")
      .eq("organization_id", currentOrgId).eq("status", "active");
    const ids = (members || []).map((m) => m.user_id);
    if (ids.length === 0) return setStaff([]);
    const { data: profs } = await supabase.from("profiles").select("id, full_name").in("id", ids);
    const byId = Object.fromEntries((profs || []).map((p) => [p.id, p]));
    setStaff((members || []).map((m) => ({
      user_id: m.user_id, role: m.role,
      full_name: byId[m.user_id]?.full_name || "Team member",
    })));
  };

  useEffect(() => { if (clientId && currentOrgId) { load(); loadStaff(); } /* eslint-disable-next-line */ }, [clientId, currentOrgId]);

  const openNew = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    // Reconstruct date/start/end from stored timestamps
    const start = v.start_time || v.occurred_at;
    const end = v.end_time || (v.occurred_at && v.duration_minutes
      ? new Date(new Date(v.occurred_at).getTime() + v.duration_minutes * 60000).toISOString()
      : null);
    const d = start ? new Date(start) : new Date();
    const e = end ? new Date(end) : new Date(d.getTime() + 30 * 60000);
    setForm({
      date: d.toISOString().slice(0, 10),
      start: d.toTimeString().slice(0, 5),
      end: e.toTimeString().slice(0, 5),
      staff_user_id: v.staff_user_id || "",
      staff_role: v.staff_role || "Care Coordinator",
      service_type: v.service_type || "consultation",
      notes: v.summary || "",
    });
    setOpen(true);
  };

  const totalHoursLive = useMemo(() => {
    const s = isoAt(form.date, form.start);
    const e = isoAt(form.date, form.end);
    return hoursBetween(s, e);
  }, [form.date, form.start, form.end]);

  const save = async () => {
    const startIso = isoAt(form.date, form.start);
    const endIso = isoAt(form.date, form.end);
    if (!startIso || !endIso) return toast.error("Date, start time and end time are required");
    if (new Date(endIso) <= new Date(startIso)) return toast.error("End time must be after start time");
    if (!form.staff_user_id) return toast.error("Select a staff member");

    const durationMin = Math.round((new Date(endIso) - new Date(startIso)) / 60000);

    const payload = {
      service_type: form.service_type,
      occurred_at: startIso,
      start_time: startIso,
      end_time: endIso,
      duration_minutes: durationMin,
      staff_user_id: form.staff_user_id,
      staff_role: form.staff_role || null,
      status: "completed",
      summary: form.notes || null,
    };
    try {
      if (editing) {
        await apiFetch(`/visits/${editing.id}`, { method: "PATCH", body: JSON.stringify(payload) });
        toast.success("Care time updated");
      } else {
        await apiFetch("/visits", { method: "POST",
          body: JSON.stringify({ ...payload, organization_id: currentOrgId, client_id: clientId }) });
        toast.success("Care time recorded");
      }
      setOpen(false); load(); onChange && onChange();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete this ${v.service_type.replace(/_/g, " ")} record?`)) return;
    try {
      await apiFetch(`/visits/${v.id}`, { method: "DELETE" });
      toast.success("Care time removed"); load(); onChange && onChange();
    } catch (e) { toast.error(e.message); }
  };

  const totalRecordedHours = rows.reduce((s, v) => s + (v.duration_minutes || 0), 0) / 60;

  if (loading) return <div className="text-center py-12 text-sm text-[#57534E]">Loading care time…</div>;

  return (
    <div className="space-y-4" data-testid="visits-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display font-semibold text-lg tracking-tight">Care / Service Time</h3>
          <p className="text-xs text-[#57534E]">Time logged for this client. Total recorded: <b className="text-[#1C1917]">{fmtHours(totalRecordedHours)}</b></p>
        </div>
        {!isReadOnly && (
          <Button data-testid="new-visit-btn" onClick={openNew} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">
            <Plus size={14} className="mr-1.5" /> Log care time
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-[#E8E2D9]">
          <Activity size={22} className="mx-auto text-[#A8A29E] mb-2" strokeWidth={1.5} />
          <p className="text-sm font-medium">No care time logged</p>
          <p className="text-xs text-[#57534E] mt-1">Log the first entry to start tracking service hours for this client.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF8F5] text-xs uppercase tracking-widest text-[#57534E]">
              <tr>
                <th className="text-left px-5 py-3">Date</th>
                <th className="text-left px-5 py-3">Start</th>
                <th className="text-left px-5 py-3">End</th>
                <th className="text-right px-5 py-3">Total hours</th>
                <th className="text-left px-5 py-3">Service</th>
                <th className="text-left px-5 py-3">Staff</th>
                <th className="text-left px-5 py-3">Role</th>
                {!isReadOnly && <th />}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E2D9]">
              {rows.map((v) => {
                const startTs = v.start_time || v.occurred_at;
                const endTs = v.end_time || (v.duration_minutes && startTs
                  ? new Date(new Date(startTs).getTime() + v.duration_minutes * 60000).toISOString() : null);
                return (
                  <tr key={v.id} data-testid={`visit-${v.id}`} className="hover:bg-[#FAF8F5] cursor-pointer" onClick={() => !isReadOnly && openEdit(v)}>
                    <td className="px-5 py-3">{formatDate(startTs)}</td>
                    <td className="px-5 py-3">{fmtTime(startTs)}</td>
                    <td className="px-5 py-3">{fmtTime(endTs)}</td>
                    <td className="px-5 py-3 text-right font-medium">{fmtHours((v.duration_minutes || 0) / 60)}</td>
                    <td className="px-5 py-3 capitalize">{v.service_type.replace(/_/g, " ")}</td>
                    <td className="px-5 py-3">{v.profiles?.full_name || "—"}</td>
                    <td className="px-5 py-3 text-[#57534E]">{v.staff_role || "—"}</td>
                    {!isReadOnly && (
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" data-testid={`edit-visit-${v.id}`} onClick={(e) => { e.stopPropagation(); openEdit(v); }}><Edit size={13} /></Button>
                        <Button size="sm" variant="ghost" data-testid={`del-visit-${v.id}`} onClick={(e) => { e.stopPropagation(); remove(v); }} className="text-red-600"><Trash2 size={13} /></Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editing ? "Edit care time" : "Log care time"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Staff member *</Label>
              <div className="mt-1.5">
                <Typeahead testid="visit-staff-typeahead"
                  options={staff.map((s) => ({ value: s.user_id, label: s.full_name, sub: s.role }))}
                  value={form.staff_user_id}
                  onChange={(v) => setForm({ ...form, staff_user_id: v || "" })}
                  placeholder="Search staff…" />
              </div>
            </div>
            <div>
              <Label>Role *</Label>
              <div className="mt-1.5">
                <Typeahead testid="visit-role-typeahead"
                  options={STAFF_ROLES.map((r) => ({ value: r, label: r }))}
                  value={form.staff_role}
                  onChange={(v) => setForm({ ...form, staff_role: v || "Care Coordinator" })}
                  onCreate={(custom) => setForm({ ...form, staff_role: custom })}
                  createLabel="Use custom role"
                  placeholder="Search or type a role…" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Date *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="mt-1.5" data-testid="visit-date" />
              </div>
              <div><Label>Start time *</Label>
                <Input type="time" value={form.start} onChange={(e) => setForm({...form, start: e.target.value})} className="mt-1.5" data-testid="visit-start" />
              </div>
              <div><Label>End time *</Label>
                <Input type="time" value={form.end} onChange={(e) => setForm({...form, end: e.target.value})} className="mt-1.5" data-testid="visit-end" />
              </div>
            </div>
            <div className="bg-[#FAF8F5] rounded-lg p-3 flex items-center justify-between border border-[#E8E2D9]">
              <span className="text-sm text-[#57534E]">Total hours</span>
              <span className="text-lg font-display font-bold" data-testid="visit-total-hours">
                {totalHoursLive > 0 ? fmtHours(totalHoursLive) : <span className="text-[#A8A29E] text-sm font-normal">Enter start &amp; end time</span>}
              </span>
            </div>
            <div>
              <Label>Service / task *</Label>
              <div className="mt-1.5">
                <Typeahead testid="visit-service-typeahead"
                  options={SERVICE_TYPES.map((s) => ({ value: s, label: s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) }))}
                  value={form.service_type}
                  onChange={(v) => setForm({ ...form, service_type: v || "consultation" })}
                  onCreate={(custom) => setForm({ ...form, service_type: custom.toLowerCase().replace(/\s+/g, "_") })}
                  createLabel="Use custom service"
                  placeholder="Search or type a service…" />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="mt-1.5" placeholder="What was covered during this time?" data-testid="visit-notes" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            {editing && (
              <Button variant="outline" onClick={() => remove(editing).then(() => setOpen(false))}
                      className="rounded-full text-red-600 border-red-200 mr-auto" data-testid="delete-visit-btn">
                <Trash2 size={13} className="mr-1" /> Delete
              </Button>
            )}
            <Button onClick={() => setOpen(false)} variant="outline" className="rounded-full">Cancel</Button>
            <Button data-testid="save-visit-btn" onClick={save} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">{editing ? "Save changes" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
